import React, { useEffect } from "react";
import {
  NavBar, StatusBadge, Button, SectionHeader, Reveal, Card,
  BeforeAfter, DecisionBlock, CodeBlock, Footer, Icon,
} from "../components/rivplay";
import { LINKS, T, UPLOAD } from "../content/home";
import { SIGNED, VALIDATION, WHATSAPP, GCP } from "../content/caseStudyIAClinical";
import { ServiceTiles } from "../components/ServiceTiles";

const INTRO = [
  ["Contexto", "Healthtech chilena orientada al historial clínico digital y a la atención asistida por inteligencia artificial."],
  ["Problema", "Hacían falta servicios para convertir voz en insumos para la IA y para auditar casos clínicos asignándolos al especialista correcto, además de una forma más eficiente de subir documentos."],
  ["Mi rol", "Diseñé y construí 2 microservicios desde cero, contribuí a varios más de la plataforma y trabajé en su infraestructura sobre Google Cloud."],
];

const RESULTS = [
  ["server", "Microservicios en producción", "Voz y auditoría clínica, más aportes a otros servicios de la plataforma."],
  ["upload", "Subidas más rápidas", "Archivos directo a Cloud Storage; menos carga en el servidor."],
  ["chat", "Especialistas avisados por WhatsApp", "Reciben el caso asignado sin tener que abrir la plataforma."],
];

export function CaseStudyIAClinical({ lang, onLang }) {
  const t = T[lang];
  // Desde esta página los enlaces de la navegación vuelven a las secciones de la home.
  const nav = t.nav.map((l) => ({ ...l, href: "/" + l.href }));

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="hp" lang="es">
      <NavBar links={nav} lang={lang} onLang={onLang} cvLabel={t.cv} cvHref={LINKS.cv} active="/#proyectos" />
      <main>
        <header className="cs-head">
          <div className="hp-hero-bg rp-aurora" aria-hidden="true" />
          <div className="rp-container cs-head-in is-text-only">
            <div>
              <a className="cs-back" href="/#proyectos"><Icon name="arrow-right" size={14} />Proyectos</a>
              <Reveal>
                <p className="rp-eyebrow">
                  <span className="rp-eyebrow-idx">Caso de estudio</span>
                  <span className="rp-eyebrow-rule" />
                  Healthtech · IA
                </p>
                <h1 className="rp-display-l cs-title">
                  IAClinical: microservicios, <span className="rp-accent">Google Cloud</span> y subidas directas a Storage.
                </h1>
              </Reveal>
            </div>
          </div>
          <div className="rp-container">
            <Reveal delay={120}>
              <dl className="cs-meta">
                <div><dt>Rol</dt><dd>Backend Developer Junior</dd></div>
                <div><dt>Periodo</dt><dd>11/2025 — hoy</dd></div>
                <div><dt>Stack</dt><dd>NestJS · PostgreSQL · Google Cloud</dd></div>
                <div><dt>Estado</dt><dd><StatusBadge>En producción</StatusBadge></dd></div>
              </dl>
            </Reveal>
          </div>
        </header>

        <section className="rp-section">
          <div className="rp-container">
            <div className="cs-intro">
              {INTRO.map(([k, v], i) => (
                <Reveal key={k} delay={i * 60}>
                  <Card>
                    <p className="rp-eyebrow">
                      <span className="rp-eyebrow-idx">0{i + 1}</span>
                      <span className="rp-eyebrow-rule" />
                      {k}
                    </p>
                    <p className="cs-intro-p">{v}</p>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="rp-section" style={{ paddingTop: 0 }}>
          <div className="rp-container">
            <Reveal>
              <SectionHeader
                index="04"
                eyebrow="Decisiones técnicas"
                title="Qué hice y por qué"
                lead="Los fragmentos de código son ilustrativos: el código del cliente es privado."
              />
            </Reveal>
            <DecisionBlock
              index="4.1"
              title="Subida de archivos con URLs firmadas"
              problem="Los archivos viajaban del frontend al backend y de ahí a Cloud Storage: cada subida consumía memoria y tiempo del servidor."
              decision="El backend entrega una URL firmada de corta duración, el frontend sube el archivo directo a Cloud Storage y el backend solo recibe los datos del registro."
              result="Menos carga en el servidor y subidas más rápidas."
              diagram={<BeforeAfter before={UPLOAD.es.before} after={UPLOAD.es.after} />}
              code={<CodeBlock filename="uploads.service.ts" code={SIGNED} highlightLines={[8, 18]} />}
            />
            <DecisionBlock
              index="4.2"
              title="Microservicios de voz y auditoría clínica"
              problem="La plataforma necesitaba convertir voz para sus flujos con IA y auditar casos clínicos con asignación automática a médicos especialistas."
              decision="Dos microservicios en NestJS diseñados desde cero, con DTOs validados de forma estricta y contratos de API claros para el resto de la plataforma."
              result="Ambos en producción; además contribuí al desarrollo de varios microservicios más."
              code={<CodeBlock filename="main.ts" code={VALIDATION} highlightLines={[4, 5]} />}
            />
            <DecisionBlock
              index="4.3"
              title="Infraestructura sobre Google Cloud"
              problem="Los servicios debían correr, escalar y ejecutar tareas periódicas sin mantenimiento manual."
              decision="Servicios en contenedores Docker sobre Cloud Run, datos en Cloud SQL, archivos en Cloud Storage y tareas programadas con Cloud Scheduler."
              result="Integración continua desde GitHub y despliegues autónomos a preproducción y producción."
              diagram={<ServiceTiles items={GCP} />}
            />
            <DecisionBlock
              index="4.4"
              title="Mensajería por WhatsApp con Twilio"
              problem="Los especialistas necesitaban enterarse de un caso asignado sin tener que abrir la plataforma."
              decision="Integración con Twilio para enviar y recibir mensajes de WhatsApp desde los microservicios."
              result="El especialista recibe el caso asignado por WhatsApp, sin abrir la plataforma."
              code={<CodeBlock filename="notifications.service.ts" code={WHATSAPP} highlightLines={[3]} />}
            />
          </div>
        </section>

        <section className="rp-section">
          <div className="rp-container">
            <Reveal><SectionHeader index="05" eyebrow="Resultado" title="Qué quedó funcionando" /></Reveal>
            <div className="cs-results">
              {RESULTS.map(([icon, v, l], i) => (
                <Reveal key={v} delay={i * 60}>
                  <Card className="cs-result">
                    <span className="cs-service-icon"><Icon name={icon} size={18} /></span>
                    <span className="rp-h3">{v}</span>
                    <span className="rp-body-s">{l}</span>
                  </Card>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <Card className="cs-next" style={{ marginTop: 48 }}>
                <div>
                  <p className="rp-eyebrow">Siguiente proyecto</p>
                  <p className="rp-h2" style={{ marginTop: 8 }}>Roam Survival</p>
                </div>
                <Button variant="secondary" href="/#proyectos" iconRight="arrow-right">Ver caso</Button>
              </Card>
            </Reveal>
            <div style={{ marginTop: 72 }}>
              <Footer text={t.footer} />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
